import React, { useState } from 'react'
import axios from 'axios'
import { getBaseURL } from '../../helpers/api'
import * as url from '../../helpers/url_helper'

interface ModuleTypeDef {
    label: string
    typeCode: number
    panelSize: number
    elements: number          // total mesh unicasts the module reserves
    entries: number           // count of items in switches[] payload
    isPanelMounted: boolean
    entryDefaultPrefix: string
    // 1-based offsets within the module (load first, switch second)
    outputOffsets: number[]   // relative element no of each appliance/load (1=primary)
    inputOffsets: number[]    // relative element no of each switch UI (0 if no UI input, e.g. controller)
}

// Element layout per spec:
//   2Sw  → L1=1, L2=2, S1=3, S2=4   (loads first then switches)
//   Knob → L1=1, S1=2                (level then regulator)
//   3R1T → L1=1, L2=2, L3=3, L4=4    (controller, no switch UI)
const MODULE_TYPES: Record<string, ModuleTypeDef> = {
    '2Sw':     { label: '2Sw',     typeCode: 0, panelSize: 2, elements: 4, entries: 2, isPanelMounted: true,  entryDefaultPrefix: 'Switch', outputOffsets: [1, 2], inputOffsets: [3, 4] },
    'Knob':    { label: 'Knob',    typeCode: 2, panelSize: 1, elements: 2, entries: 1, isPanelMounted: true,  entryDefaultPrefix: 'Switch', outputOffsets: [1],    inputOffsets: [2] },
    'KnobPro': { label: 'KnobPro', typeCode: 3, panelSize: 1, elements: 2, entries: 1, isPanelMounted: true,  entryDefaultPrefix: 'Switch', outputOffsets: [1],    inputOffsets: [2] },
    '3R1T':    { label: '3R1T',    typeCode: 4, panelSize: 4, elements: 4, entries: 4, isPanelMounted: false, entryDefaultPrefix: 'Output', outputOffsets: [1, 2, 3, 4], inputOffsets: [0, 0, 0, 0] },
}

const PLACEHOLDER_IMAGE = 'https://placehold.co/200x200'
const DUMMY_LOCATION = '18.5204,73.8567'
const DUMMY_LOCATION_LABEL = 'Pune (Test)'
const STATIC_OTP_VALUE = '9999'

interface SessionPlace { id: string; name: string }
interface SessionRoom { id: string; name: string; placeId: string }
interface SessionModule { id: string; name: string; type: string; mac: string; roomId: string }

// Reverse-map a numeric PCB type code ('0', '2', 4, etc.) to our string label
const typeCodeToLabel = (code: string | number | undefined): string => {
    const codeStr = String(code ?? '')
    const match = Object.entries(MODULE_TYPES).find(([_, def]) => String(def.typeCode) === codeStr)
    return match ? match[0] : codeStr || '?'
}

const Card = ({ title, children, error }: { title: string; children: React.ReactNode; error?: string }) => (
    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 24, marginBottom: 16 }}>
        <h5 style={{ marginTop: 0, marginBottom: 16, fontSize: 16, fontWeight: 600 }}>{title}</h5>
        {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', padding: 10, borderRadius: 6, marginBottom: 12, color: '#991B1B', fontSize: 13 }}>
                ✗ {error}
            </div>
        )}
        {children}
    </div>
)

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>{label}</label>
        {children}
    </div>
)

const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input
        {...props}
        style={{
            width: '100%', maxWidth: 400, padding: '8px 12px', fontSize: 14,
            border: '1px solid #D1D5DB', borderRadius: 6, ...props.style,
        }}
    />
)

const Select = (props: React.SelectHTMLAttributes<HTMLSelectElement>) => (
    <select
        {...props}
        style={{
            width: '100%', maxWidth: 400, padding: '8px 12px', fontSize: 14,
            border: '1px solid #D1D5DB', borderRadius: 6, ...props.style,
        }}
    />
)

const PrimaryButton = ({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
    <button
        {...props}
        style={{
            padding: '8px 20px', fontSize: 14, fontWeight: 500,
            background: props.disabled ? '#9CA3AF' : '#111827', color: '#fff',
            border: 'none', borderRadius: 6, cursor: props.disabled ? 'not-allowed' : 'pointer',
            ...props.style,
        }}
    >
        {children}
    </button>
)

const Pill = ({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) => (
    <button
        onClick={onClick}
        style={{
            padding: '6px 14px', fontSize: 13, fontWeight: active ? 600 : 400,
            background: active ? '#111827' : '#fff',
            color: active ? '#fff' : '#374151',
            border: '1px solid ' + (active ? '#111827' : '#D1D5DB'),
            borderRadius: 16, cursor: 'pointer',
            marginRight: 6, marginBottom: 6,
        }}
    >
        {children}
    </button>
)

const TestUtilities: React.FC = () => {
    // Activity log (shared)
    const [eventLog, setEventLog] = useState<string[]>([])
    const log = (line: string) => setEventLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${line}`])

    // --- USER ---
    const [emailOrMobile, setEmailOrMobile] = useState('')
    const [useStaticOtp, setUseStaticOtp] = useState(true)
    const [otpSent, setOtpSent] = useState(false)
    const [otp, setOtp] = useState('')
    const [userToken, setUserToken] = useState<string | null>(null)
    const [userId, setUserId] = useState<string | null>(null)
    const [userBusy, setUserBusy] = useState(false)
    const [userError, setUserError] = useState('')

    // --- PLACES ---
    const [places, setPlaces] = useState<SessionPlace[]>([])
    const [activePlaceId, setActivePlaceId] = useState<string | null>(null)
    const [newPlaceName, setNewPlaceName] = useState('')
    const [placeBusy, setPlaceBusy] = useState(false)
    const [placeError, setPlaceError] = useState('')

    // --- ROOMS ---
    const [rooms, setRooms] = useState<SessionRoom[]>([])
    const [activeRoomId, setActiveRoomId] = useState<string | null>(null)
    const [newRoomName, setNewRoomName] = useState('')
    const [roomBusy, setRoomBusy] = useState(false)
    const [roomError, setRoomError] = useState('')

    // --- MODULES ---
    const [modules, setModules] = useState<SessionModule[]>([])
    const [moduleName, setModuleName] = useState('')
    const [moduleType, setModuleType] = useState<string>('Knob')
    const [moduleMac, setModuleMac] = useState('')
    const [applianceNames, setApplianceNames] = useState<string[]>([''])
    const [moduleBusy, setModuleBusy] = useState(false)
    const [moduleError, setModuleError] = useState('')

    const onModuleTypeChange = (newType: string) => {
        setModuleType(newType)
        const def = MODULE_TYPES[newType]
        setApplianceNames(Array(def?.entries || 1).fill(''))
    }

    // ---------- HTTP helpers (use test-user JWT) ----------
    const post = async (path: string, body: any) =>
        axios.post(`${getBaseURL()}${path}`, body, {
            headers: userToken ? { Authorization: `Bearer ${userToken}` } : {},
        })
    const get = async (path: string, params?: any) =>
        axios.get(`${getBaseURL()}${path}`, {
            headers: userToken ? { Authorization: `Bearer ${userToken}` } : {},
            params,
        })
    const apiErrorMsg = (e: any) =>
        e?.response?.data?.errors?.[0]?.msg
        || e?.response?.data?.msg
        || e?.response?.data?.message
        || e?.message
        || 'Request failed'

    // ---------- USER actions ----------
    const sendOtp = async () => {
        setUserError(''); setUserBusy(true)
        try {
            if (useStaticOtp) {
                const adminToken = localStorage.getItem('Authorization')
                await axios.post(`${getBaseURL()}${url.ADD_STATIC_OTP}`,
                    { email_or_mobile: emailOrMobile, otp: STATIC_OTP_VALUE },
                    { headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {} }
                )
                log(`✓ Static OTP ${STATIC_OTP_VALUE} set for ${emailOrMobile}`)
            }
            await axios.post(`${getBaseURL()}${url.APP_SEND_OTP}`, { email_or_mobile: emailOrMobile })
            setOtpSent(true)
            if (useStaticOtp) setOtp(STATIC_OTP_VALUE)
            log(`✓ OTP sent to ${emailOrMobile}${useStaticOtp ? ' (no real email — static OTP active)' : ''}`)
        } catch (e: any) { const m = apiErrorMsg(e); setUserError(m); log(`✗ ${m}`) }
        finally { setUserBusy(false) }
    }

    const verifyOtp = async () => {
        setUserError(''); setUserBusy(true)
        try {
            const res = await axios.post(`${getBaseURL()}${url.APP_VERIFY_OTP}`, {
                email_or_mobile: emailOrMobile,
                otp,
                appVersion: 'admin-test-ui-1.0',
                osName: 'web',
                osVersion: '1.0',
                macAddress: 'TEST-' + emailOrMobile,
            })
            const token = res.data?.token
            const id = res.data?.data?._id || res.data?.data?.id
            if (!token) throw new Error('No token in verify-otp response')
            setUserToken(token)
            setUserId(id)
            log(`✓ Verified — userId ${id}`)
            await loadExistingHierarchy(token)
        } catch (e: any) { const m = apiErrorMsg(e); setUserError(m); log(`✗ ${m}`) }
        finally { setUserBusy(false) }
    }

    // Fetch the user's existing places / rooms / modules and pre-populate
    // session state. Lets the user select existing entities instead of
    // having to create everything from scratch each session.
    const loadExistingHierarchy = async (token: string) => {
        try {
            log(`→ GET ${url.APP_PROFILE}`)
            const res = await axios.get(`${getBaseURL()}${url.APP_PROFILE}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            const ud = res?.data?.userData || res?.data?.data || res?.data || {}
            const rawPlaces: any[] = ud.places || []
            const rawRooms: any[]  = ud.rooms  || []
            const rawRelays: any[] = ud.relays || []
            const rawPcbs: any[]   = ud.pcbs   || []

            const relayMap: Record<string, string> = Object.fromEntries(
                rawRelays.map((r: any) => [r._id || r.id, r.name])
            )

            setPlaces(rawPlaces.map((p: any) => ({
                id: p._id || p.id,
                name: p.name,
            })))
            setRooms(rawRooms.map((r: any) => ({
                id: r._id || r.id,
                name: r.name,
                placeId: r.placeId,
            })))
            setModules(rawPcbs.map((p: any) => ({
                id: p._id || p.id,
                name: relayMap[p.relayId] || '(unnamed panel)',
                type: typeCodeToLabel(p.type),
                mac: p.macAddress || '',
                roomId: p.roomId,
            })))
            log(`✓ Loaded ${rawPlaces.length} place(s), ${rawRooms.length} room(s), ${rawPcbs.length} module(s)`)
        } catch (e: any) {
            // Non-fatal — user can still create new from scratch
            log(`✗ Could not load existing data: ${apiErrorMsg(e)} (user can still create new)`)
        }
    }

    const signOut = () => {
        setUserToken(null); setUserId(null); setOtpSent(false); setOtp(''); setEmailOrMobile('')
        setPlaces([]); setActivePlaceId(null); setNewPlaceName(''); setPlaceError('')
        setRooms([]); setActiveRoomId(null); setNewRoomName(''); setRoomError('')
        setModules([]); setModuleName(''); setModuleMac(''); setModuleType('Knob'); setApplianceNames(['']); setModuleError('')
        log('✓ Signed out')
    }

    // ---------- PLACE actions ----------
    const createPlace = async () => {
        setPlaceError(''); setPlaceBusy(true)
        try {
            const res = await post(url.APP_ADD_PLACE, {
                name: newPlaceName,
                location: DUMMY_LOCATION,
                locationLabel: DUMMY_LOCATION_LABEL,
                image: PLACEHOLDER_IMAGE,
            })
            const id = res.data?.data?._id || res.data?.data?.id
            if (!id) throw new Error('No place id in response')
            const place = { id, name: newPlaceName }
            setPlaces(prev => [...prev, place])
            setActivePlaceId(id)
            setNewPlaceName('')
            log(`✓ Place created — ${place.name} (${id})`)
        } catch (e: any) { const m = apiErrorMsg(e); setPlaceError(m); log(`✗ ${m}`) }
        finally { setPlaceBusy(false) }
    }

    const deletePlace = async (placeIdToDelete: string, placeNameToDelete: string) => {
        if (!window.confirm(`Delete "${placeNameToDelete}" and all its rooms / modules on staging?\n\nThis hits POST /api/v1/place/delete and cannot be undone.`)) return
        setPlaceError(''); setPlaceBusy(true)
        try {
            log(`→ POST ${url.APP_DELETE_PLACE} body: ${JSON.stringify({ placeId: placeIdToDelete })}`)
            const res = await post(url.APP_DELETE_PLACE, { placeId: placeIdToDelete })
            log(`← ${JSON.stringify(res.data)}`)
            setPlaces(prev => prev.filter(p => p.id !== placeIdToDelete))
            setRooms(prev => prev.filter(r => r.placeId !== placeIdToDelete))
            const removedRoomIds = rooms.filter(r => r.placeId === placeIdToDelete).map(r => r.id)
            setModules(prev => prev.filter(m => !removedRoomIds.includes(m.roomId)))
            if (activePlaceId === placeIdToDelete) {
                setActivePlaceId(null)
                setActiveRoomId(null)
            }
            log(`✓ Place deleted — ${placeNameToDelete}`)
        } catch (e: any) { const m = apiErrorMsg(e); setPlaceError(m); log(`✗ ${m}`) }
        finally { setPlaceBusy(false) }
    }

    // ---------- ROOM actions ----------
    const addRoom = async () => {
        if (!activePlaceId) return
        setRoomError(''); setRoomBusy(true)
        try {
            const res = await post(url.APP_ADD_ROOM, {
                placeId: activePlaceId,
                name: newRoomName,
                image: PLACEHOLDER_IMAGE,
            })
            const id = res.data?.data?._id || res.data?.data?.id
            if (!id) throw new Error('No room id in response')
            const room = { id, name: newRoomName, placeId: activePlaceId }
            setRooms(prev => [...prev, room])
            setActiveRoomId(id)
            setNewRoomName('')
            log(`✓ Room added — ${room.name} (${id})`)
        } catch (e: any) { const m = apiErrorMsg(e); setRoomError(m); log(`✗ ${m}`) }
        finally { setRoomBusy(false) }
    }

    // ---------- MODULE actions ----------
    const addModule = async () => {
        if (!activeRoomId || !activePlaceId) return
        setModuleError(''); setModuleBusy(true)
        try {
            const def = MODULE_TYPES[moduleType]
            if (!def) throw new Error('Unknown module type')

            log(`→ GET ${url.APP_GET_LAST_UNICAST}?placeId=${activePlaceId}`)
            const lastRes = await get(url.APP_GET_LAST_UNICAST, { placeId: activePlaceId })
            log(`← ${JSON.stringify(lastRes.data)}`)
            const lastUnicast: number = Number(lastRes.data?.lastAssignedUnicastAddress) || 0
            log(`  parsed lastUnicast = ${lastUnicast}`)

            // User occupies unicast 1; first module on fresh place starts at 2
            const baseUnicast = Math.max(lastUnicast + 1, 2)
            const switches = []
            for (let i = 0; i < def.entries; i++) {
                switches.push({
                    type: 0,
                    name: applianceNames[i]?.trim() || `${def.entryDefaultPrefix}-${i + 1}`,
                    no: i + 1,
                    icon: '',
                    inputElementNo: def.inputOffsets[i],
                    outputElementNo: def.outputOffsets[i],
                    mode: 0,
                })
            }

            const pcbBody = {
                roomId: activeRoomId,
                name: moduleName,
                panelSize: def.panelSize,
                pcb: [{
                    macAddress: moduleMac,
                    unicastAddress: baseUnicast,
                    type: def.typeCode,
                    isAdvertiser: false,
                    advertiserMacAddress: moduleMac,
                    advertiserType: def.typeCode,
                    ssId: 'test-ssid',
                    password: 'test-password',
                    switches,
                }],
            }
            log(`→ POST ${url.APP_ADD_RELAY} (type=${moduleType}, mac=${moduleMac}, baseUnicast=${baseUnicast})`)
            log(`  body: ${JSON.stringify(pcbBody)}`)
            const relayRes = await post(url.APP_ADD_RELAY, pcbBody)
            log(`← ${JSON.stringify(relayRes.data)}`)
            const newRelayId = relayRes.data?.data?.id || relayRes.data?.data?._id
            log(`✓ Module added — relayId ${newRelayId}`)

            const newLast = baseUnicast + def.elements - 1
            const updateBody = { placeId: activePlaceId, lastAssignedUnicastAddress: newLast }
            log(`→ POST ${url.APP_UPDATE_LAST_UNICAST} body: ${JSON.stringify(updateBody)}`)
            const updateRes = await post(url.APP_UPDATE_LAST_UNICAST, updateBody)
            log(`← ${JSON.stringify(updateRes.data)}`)
            log(`✓ Updated last unicast to ${newLast}`)

            setModules(prev => [...prev, { id: newRelayId, name: moduleName, type: moduleType, mac: moduleMac, roomId: activeRoomId }])
            setModuleName(''); setModuleMac(''); setApplianceNames(Array(def.entries).fill(''))
        } catch (e: any) { const m = apiErrorMsg(e); setModuleError(m); log(`✗ ${m}`) }
        finally { setModuleBusy(false) }
    }

    const isStaging = localStorage.getItem('dayro_server') === 'staging'

    const activePlace = places.find(p => p.id === activePlaceId)
    const activeRoom = rooms.find(r => r.id === activeRoomId)
    const placeRooms = rooms.filter(r => r.placeId === activePlaceId)
    const roomModules = modules.filter(m => m.roomId === activeRoomId)
    const moduleTypeDef = MODULE_TYPES[moduleType]

    const moduleFormValid = !moduleBusy
        && moduleName.trim() !== ''
        && moduleMac.trim() !== ''
        && applianceNames.slice(0, moduleTypeDef?.entries || 0).every(n => n.trim() !== '')

    return (
        <div className="page-content">
            <div style={{ padding: 24 }}>
                <div style={{ marginBottom: 24 }}>
                    <h3 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>🧪 Test Utilities</h3>
                    <p style={{ marginTop: 4, color: '#6B7280', fontSize: 13 }}>
                        Create test data on staging by simulating the App's user flows. Each section is independent — errors in one don't reset the others. Refreshing the page resets everything.
                    </p>
                </div>

                {!isStaging && (
                    <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                        <strong style={{ color: '#991B1B' }}>Staging only.</strong>{' '}
                        <span style={{ color: '#7F1D1D', fontSize: 14 }}>
                            This page is only meant to be used when the Staging server is selected on the login page.
                        </span>
                    </div>
                )}

                {/* ===== USER CARD ===== */}
                <Card title="Test User" error={userError}>
                    {!userToken ? (
                        <>
                            <Field label="Email or 10-digit mobile">
                                <Input
                                    value={emailOrMobile}
                                    onChange={e => setEmailOrMobile(e.target.value)}
                                    placeholder="qa+test@dayro.com"
                                    disabled={otpSent}
                                />
                            </Field>
                            {!otpSent && (
                                <div style={{ marginBottom: 14, padding: 10, background: useStaticOtp ? '#FEF3C7' : '#F9FAFB', border: `1px solid ${useStaticOtp ? '#FCD34D' : '#E5E7EB'}`, borderRadius: 6 }}>
                                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', fontSize: 13 }}>
                                        <input
                                            type="checkbox"
                                            checked={useStaticOtp}
                                            onChange={e => setUseStaticOtp(e.target.checked)}
                                            style={{ marginTop: 3 }}
                                        />
                                        <span>
                                            <strong>Use static OTP {STATIC_OTP_VALUE} for this email</strong>
                                            <div style={{ color: '#6B7280', fontSize: 12, marginTop: 2 }}>
                                                {useStaticOtp
                                                    ? `No real email will be sent. Wizard will set static OTP ${STATIC_OTP_VALUE} on staging first, then trigger Send OTP.`
                                                    : '⚠ A real email may be sent to this address if no static OTP exists.'}
                                            </div>
                                        </span>
                                    </label>
                                </div>
                            )}
                            {!otpSent ? (
                                <PrimaryButton onClick={sendOtp} disabled={userBusy || !emailOrMobile}>
                                    {userBusy ? 'Sending…' : 'Send OTP'}
                                </PrimaryButton>
                            ) : (
                                <>
                                    <Field label="OTP (4 digits)">
                                        <Input value={otp} onChange={e => setOtp(e.target.value)} placeholder="9999" maxLength={6} />
                                    </Field>
                                    <PrimaryButton onClick={verifyOtp} disabled={userBusy || !otp}>
                                        {userBusy ? 'Verifying…' : 'Verify OTP'}
                                    </PrimaryButton>
                                    <button onClick={() => { setOtpSent(false); setOtp(''); setUserError('') }} style={{ marginLeft: 12, background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 13 }}>
                                        ← Change email
                                    </button>
                                </>
                            )}
                        </>
                    ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                            <div style={{ fontSize: 14, color: '#374151' }}>
                                Logged in as <strong>{emailOrMobile}</strong> · userId <code style={{ fontSize: 12 }}>{userId}</code>
                            </div>
                            <button onClick={signOut} style={{ padding: '6px 14px', fontSize: 13, background: '#fff', color: '#374151', border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer' }}>
                                Sign out
                            </button>
                        </div>
                    )}
                </Card>

                {/* ===== PLACES CARD ===== */}
                {userToken && (
                    <Card title="Places" error={placeError}>
                        {places.length > 0 && (
                            <div style={{ marginBottom: 14 }}>
                                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>Active place (click to switch, × to delete):</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {places.map(p => (
                                        <div key={p.id} style={{ display: 'inline-flex', alignItems: 'stretch' }}>
                                            <Pill active={p.id === activePlaceId} onClick={() => { setActivePlaceId(p.id); setActiveRoomId(null) }}>
                                                {p.name}
                                            </Pill>
                                            <button
                                                onClick={() => deletePlace(p.id, p.name)}
                                                disabled={placeBusy}
                                                title="Delete place"
                                                style={{
                                                    marginLeft: -4, marginRight: 6, marginBottom: 6,
                                                    padding: '0 10px', fontSize: 14, fontWeight: 700,
                                                    background: '#fff', color: '#B91C1C',
                                                    border: '1px solid #FCA5A5',
                                                    borderRadius: 16,
                                                    cursor: placeBusy ? 'not-allowed' : 'pointer',
                                                }}
                                            >×</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <Field label="New place name">
                            <Input value={newPlaceName} onChange={e => setNewPlaceName(e.target.value)} placeholder="Test Home" />
                        </Field>
                        <PrimaryButton onClick={createPlace} disabled={placeBusy || !newPlaceName.trim()}>
                            {placeBusy ? 'Creating…' : 'Create Place'}
                        </PrimaryButton>
                    </Card>
                )}

                {/* ===== ROOMS CARD ===== */}
                {userToken && activePlaceId && (
                    <Card title={`Rooms in ${activePlace?.name}`} error={roomError}>
                        {placeRooms.length > 0 && (
                            <div style={{ marginBottom: 14 }}>
                                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>Active room (click to switch):</div>
                                <div>
                                    {placeRooms.map(r => (
                                        <Pill key={r.id} active={r.id === activeRoomId} onClick={() => setActiveRoomId(r.id)}>
                                            {r.name}
                                        </Pill>
                                    ))}
                                </div>
                            </div>
                        )}
                        <Field label="New room name">
                            <Input value={newRoomName} onChange={e => setNewRoomName(e.target.value)} placeholder="Living Room" />
                        </Field>
                        <PrimaryButton onClick={addRoom} disabled={roomBusy || !newRoomName.trim()}>
                            {roomBusy ? 'Adding…' : 'Add Room'}
                        </PrimaryButton>
                    </Card>
                )}

                {/* ===== MODULES CARD ===== */}
                {userToken && activeRoomId && moduleTypeDef && (
                    <Card title={`Modules in ${activeRoom?.name}`} error={moduleError}>
                        {roomModules.length > 0 && (
                            <div style={{ marginBottom: 14, padding: 10, background: '#F9FAFB', borderRadius: 6 }}>
                                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>Added in this session:</div>
                                {roomModules.map(m => (
                                    <div key={m.id} style={{ fontSize: 13, color: '#374151', padding: '3px 0' }}>
                                        ✓ <strong>{m.name}</strong> ({m.type}, MAC {m.mac})
                                    </div>
                                ))}
                            </div>
                        )}
                        <Field label={moduleTypeDef.isPanelMounted ? 'Panel name (required)' : 'Controller name (required)'}>
                            <Input
                                value={moduleName}
                                onChange={e => setModuleName(e.target.value)}
                                placeholder={moduleTypeDef.isPanelMounted ? 'Living Room Panel' : 'Outdoor Controller'}
                            />
                        </Field>
                        <Field label="Module type">
                            <Select value={moduleType} onChange={e => onModuleTypeChange(e.target.value)}>
                                {Object.keys(MODULE_TYPES).map(k => <option key={k} value={k}>{MODULE_TYPES[k].label} ({MODULE_TYPES[k].elements} unicasts)</option>)}
                            </Select>
                        </Field>
                        <Field label="MAC address (must be unique system-wide)">
                            <Input value={moduleMac} onChange={e => setModuleMac(e.target.value)} placeholder="AA:BB:CC:DD:EE:FF" />
                        </Field>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
                                Appliance names ({moduleTypeDef.entries} required — one per {moduleTypeDef.isPanelMounted ? 'switch' : 'output'})
                            </label>
                            {applianceNames.slice(0, moduleTypeDef.entries).map((val, i) => (
                                <Input
                                    key={i}
                                    value={val}
                                    onChange={e => {
                                        const next = [...applianceNames]
                                        next[i] = e.target.value
                                        setApplianceNames(next)
                                    }}
                                    placeholder={`${moduleTypeDef.entryDefaultPrefix}-${i + 1}`}
                                    style={{ marginBottom: 6 }}
                                />
                            ))}
                        </div>
                        <PrimaryButton onClick={addModule} disabled={!moduleFormValid}>
                            {moduleBusy ? 'Provisioning…' : 'Add Module'}
                        </PrimaryButton>
                    </Card>
                )}

                {/* ===== ACTIVITY LOG ===== */}
                {eventLog.length > 0 && (
                    <Card title="Activity">
                        <pre style={{ background: '#F9FAFB', padding: 12, borderRadius: 6, fontSize: 12, margin: 0, maxHeight: 320, overflow: 'auto' }}>
                            {eventLog.join('\n')}
                        </pre>
                    </Card>
                )}
            </div>
        </div>
    )
}

export default TestUtilities
